import { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Button } from "@/common/components/ui/button";
import {
  ArrowLeft,
  Save,
  Loader2,
  PlayCircle,
  CheckCircle,
} from "lucide-react";
import { Card, CardContent } from "@/common/components/ui/card";
import { Input } from "@/common/components/ui/input";
import { Label } from "@/common/components/ui/label";
import AntDTable from "@/common/components/ui/antd-table";
import { Switch, message } from "antd";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/common/components/ui/select";
import finEaseHomeNavigationRoutes from "@/projects/FinEaseHomeAndAdmin/utils/navigationRoutes";
import ScreenerService from "@/services/FinEaseHomeAndAdmin/APIs/ScrennerService";
import type { AdvancedQueryField } from "@/utils/types/Screener/Screener";

interface QueryMasterAdminPayload {
  title: string;
  sub_title: string;
  category: string;
  query: string;
  user: string;
  enable: boolean;
}

interface VerifyPreviewRow {
  key: string;
  companyName: string;
  symbol: string;
  resultType: string;
  peTtm: string;
  roeTtm: string;
  debtToEquity: string;
}

// Removed staticQueries
const QUERY_OPERATOR_TOKENS = [">", "<", "=", ">=", "<=", "AND"] as const;

const AddEditQueryMasterAdmin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  const isEdit = !!id;
  const isViewMode = location.pathname.includes("/view/");

  const [formData, setFormData] = useState<QueryMasterAdminPayload>({
    title: "",
    sub_title: "",
    category: "",
    query: "",
    user: "Super Admin",
    enable: true,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [categories, setCategories] = useState<any[]>([]);
  const [advancedQueryFields, setAdvancedQueryFields] = useState<
    AdvancedQueryField[]
  >([]);
  const [verifyRows, setVerifyRows] = useState<Record<string, any>[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);

  const verifyQueryAndLoadRows = async (
    queryText: string,
    options?: { showSuccessMessage?: boolean }
  ) => {
    if (!queryText?.trim()) {
      return;
    }

    setIsVerifying(true);
    try {
      const response = await ScreenerService.executeAdvancedQuery<any>(
        {
          query: queryText,
        },
        page,
        pageSize
      );

      if (response && response.success !== false) {
        const payload = response.data;
        let rows: Record<string, any>[] = [];

        if (Array.isArray(payload)) {
          rows = payload;
        } else if (Array.isArray(payload?.results)) {
          rows = payload.results;
        } else if (Array.isArray(payload?.data)) {
          rows = payload.data;
        } else if (payload && typeof payload === "object") {
          rows = [payload];
        }

        setVerifyRows(rows);
        setTotal(payload?.count ?? rows.length);
        setIsVerified(true);
        if (options?.showSuccessMessage !== false) {
          message.success(response.message ?? "Query verified successfully.");
        }
      } else {
        setIsVerified(false);
        setVerifyRows([]);
        setTotal(0);
        message.warning(response?.message ?? "Query verification failed.");
      }
    } catch (error: any) {
      console.error(error);
      setIsVerified(false);
      setVerifyRows([]);
      setTotal(0);
      message.error(
        error?.response?.data?.message ??
          "Something went wrong while verifying query."
      );
    } finally {
      setIsVerifying(false);
    }
  };

  const fetchQueryDetails = async (queryId: string) => {
    setIsLoading(true);
    try {
      const response = await ScreenerService.getQueryMasterById(queryId);
      if (response && response.success !== false && response.data) {
        const fetchedQuery = response.data.query ?? "";
        setFormData({
          title: response.data.title ?? "",
          sub_title: response.data.sub_title ?? "",
          category: response.data.category ?? "",
          query: fetchedQuery,
          user: response.data.user ?? "Super Admin",
          enable: response.data.is_enabled ?? true,
        });
        // Assuming fetched query is valid and verified
        setIsVerified(true);
        if ((isEdit || isViewMode) && fetchedQuery.trim()) {
          await verifyQueryAndLoadRows(fetchedQuery, {
            showSuccessMessage: false,
          });
        }
      } else {
        message.warning("Failed to fetch admin query details");
      }
    } catch (error: any) {
      console.error(error);
      message.error(
        error?.response?.data?.message ??
          "Something went wrong while fetching admin query details."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCategories = async () => {
    setIsLoading(true);
    try {
      const response = await ScreenerService.getQueryCategories();
      if (response && response.success !== false) {
        setFormData(prev => ({
          ...prev,
          category: response.data.results[0].title,
        }));
        setCategories(response.data.results ?? []);
      } else {
        message.warning("Failed to fetch categories");
      }
    } catch (error: any) {
      console.error(error);
      message.error(
        error?.response?.data?.message ??
          "Something went wrong while fetching categories."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAdvancedQueryFields = async () => {
    try {
      const response = await ScreenerService.getAdvancedQueryFields();
      if (response && response.success !== false) {
        setAdvancedQueryFields(response.data?.results ?? []);
      } else {
        setAdvancedQueryFields([]);
      }
    } catch (error: any) {
      console.error(error);
      setAdvancedQueryFields([]);
      message.error(
        error?.response?.data?.message ??
          "Something went wrong while fetching query metrics."
      );
    }
  };

  useEffect(() => {
    if ((isEdit || isViewMode) && id) {
      fetchQueryDetails(id);
    }
    fetchCategories();
    fetchAdvancedQueryFields();
  }, [id, isEdit, isViewMode]);

  useEffect(() => {
    if (isVerified && !isVerifying && formData.query.trim()) {
      verifyQueryAndLoadRows(formData.query, { showSuccessMessage: false });
    }
  }, [page, pageSize]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    // If query is modified, set to unverified
    if (name === "query") {
      setIsVerified(false);
      setVerifyRows([]);
      setTotal(0);
      setPage(1);
    }
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleVerifyQuery = async () => {
    setPage(1);
    await verifyQueryAndLoadRows(formData.query);
  };

  const verifyPreviewRows = useMemo<VerifyPreviewRow[]>(() => {
    return verifyRows.map((row, index) => {
      const company = row?.company ?? {};
      const standalone = row?.ttm_standalone;
      const consolidated = row?.ttm_consolidated;
      const resultSource = standalone ?? consolidated ?? {};

      return {
        key: String(company?.id ?? row?.id ?? index),
        companyName: String(
          company?.companyname ?? company?.companyshortname ?? "-"
        ),
        co_code: String(company?.co_code ?? "-"),
        symbol: String(company?.nsesymbol ?? company?.bsecode ?? "-"),
        resultType: String(resultSource?.result_type ?? "-"),
        peTtm: String(resultSource?.pe_ttm ?? "-"),
        roeTtm: String(resultSource?.roe_ttm ?? "-"),
        debtToEquity: String(resultSource?.debt_to_equity ?? "-"),
      };
    });
  }, [verifyRows]);

  const verifyColumns = useMemo(() => {
    if (!verifyRows.length) {
      return [];
    }

    return [
      {
        title: "Company Name",
        dataIndex: "companyName",
        key: "companyName",
        width: 300,
        ellipsis: true,
      },
      {
        title: "Company Code",
        dataIndex: "co_code",
        key: "co_code",
        width: 150,
        ellipsis: true,
      },
      {
        title: "Symbol",
        dataIndex: "symbol",
        key: "symbol",
        width: 150,
        ellipsis: true,
      },
      {
        title: "Result Type",
        dataIndex: "resultType",
        key: "resultType",
        width: 150,
      },
      {
        title: "PE (TTM)",
        dataIndex: "peTtm",
        key: "peTtm",
        width: 150,
      },
      {
        title: "ROE (TTM)",
        dataIndex: "roeTtm",
        key: "roeTtm",
        width: 120,
      },
      {
        title: "Debt/Equity",
        dataIndex: "debtToEquity",
        key: "debtToEquity",
        width: 150,
      },
    ];
  }, [verifyRows.length]);

  const insertQueryToken = (token: string) => {
    if (isViewMode) {
      return;
    }

    setFormData(prev => {
      const current = prev.query.trim();
      const needsSpace = current.length > 0 ? " " : "";
      return {
        ...prev,
        query: `${current}${needsSpace}${token}`,
      };
    });
    setIsVerified(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isViewMode) {
      navigate(finEaseHomeNavigationRoutes.QUERY_MASTER_ADMIN);
      return;
    }

    if (!isVerified) {
      message.error("Please verify the query first before saving.");
      return;
    }

    setIsLoading(true);
    try {
      if (isEdit && id) {
        const response: any = await ScreenerService.updateQueryMasterById(id, {
          title: formData.title,
          sub_title: formData.sub_title,
          category: formData.category,
          query: formData.query,
          is_enabled: formData.enable,
        });

        if (response && response.success !== false) {
          message.success(
            response.message ?? "Admin Query updated successfully!"
          );
          navigate(finEaseHomeNavigationRoutes.QUERY_MASTER_ADMIN);
        } else {
          message.warning(response.message ?? "Failed to update Admin Query");
        }
      } else {
        const response: any = await ScreenerService.createQueryMaster({
          title: formData.title,
          sub_title: formData.sub_title,
          category: formData.category, // assuming string UUID category
          query: formData.query,
          is_enabled: formData.enable,
        });

        if (response && response.success !== false) {
          message.success(
            response.message ?? "Admin Query created successfully!"
          );
          navigate(finEaseHomeNavigationRoutes.QUERY_MASTER_ADMIN);
        } else {
          message.warning(response.message ?? "Failed to create Admin Query");
        }
      }
    } catch (error: any) {
      console.error(error);
      const errorData = error?.response?.data?.error ?? error?.response?.data;

      if (errorData?.fields && typeof errorData.fields === "object") {
        Object.entries(errorData.fields).forEach(([field, messages]) => {
          const msgs = Array.isArray(messages)
            ? messages.join(", ")
            : String(messages);
          const formattedField = field
            .replace(/_/g, " ")
            .replace(/\b\w/g, c => c.toUpperCase());
          message.error(`${formattedField}: ${msgs}`);
        });
      } else {
        message.error(
          errorData?.message ??
            "An error occurred while saving the Admin Query."
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-slate-50/50 font-sans text-sm">
      <form onSubmit={handleSubmit} className="flex h-full flex-col">
        <div className="sticky top-0 z-30 border-b border-slate-200 bg-white">
          <div className="mx-auto w-full max-w-5xl">
            <div className="flex items-center justify-between px-6 py-4">
              <div className="flex items-center gap-5">
                <Button
                  variant="outline"
                  size="icon"
                  type="button"
                  onClick={() =>
                    navigate(finEaseHomeNavigationRoutes.QUERY_MASTER_ADMIN)
                  }
                  className="h-10 w-10 border-slate-200 shadow-sm transition-all hover:border-slate-300 hover:bg-slate-50"
                >
                  <ArrowLeft className="h-5 w-5 text-slate-600" />
                </Button>
                <div>
                  <h1 className="text-2xl font-bold tracking-tight text-slate-900">
                    {isViewMode
                      ? `View Admin Query`
                      : isEdit
                        ? `Edit Admin Query`
                        : "Create Admin Query"}
                  </h1>
                  <p className="mt-0.5 text-sm font-medium text-slate-500">
                    Configure Screener Admin Query details
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                {isViewMode ? (
                  <Button
                    type="button"
                    variant="default"
                    onClick={() =>
                      navigate(finEaseHomeNavigationRoutes.QUERY_MASTER_ADMIN)
                    }
                    className="h-10 bg-primary px-5 text-sm font-semibold text-white"
                  >
                    Back to List
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    disabled={isLoading || !isVerified}
                    className="h-11 gap-2.5 bg-primary px-8 text-sm font-bold text-white shadow-lg shadow-primary/20 transition-all hover:-translate-y-0.5 hover:bg-primary/95"
                  >
                    {isLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4" />
                    )}
                    {isEdit ? "Save Changes" : "Create Query"}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="flex w-full flex-1 overflow-auto bg-slate-50/60">
          <div className="mx-auto w-full max-w-5xl flex-1 p-6">
            <Card className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-[0_8px_30px_rgba(15,23,42,0.08)]">
              <CardContent className="space-y-8 p-8">
                <div>
                  <h3 className="mb-6 border-b border-slate-200/80 pb-3 text-lg font-bold tracking-tight text-primary">
                    Admin Query Information
                  </h3>
                  <div className="grid grid-cols-1 gap-7 md:grid-cols-2">
                    <div className="space-y-3">
                      <Label
                        htmlFor="title"
                        className="text-sm font-semibold text-slate-700"
                      >
                        Title
                      </Label>
                      <Input
                        id="title"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        placeholder="e.g. Top IT Stocks"
                        disabled={isViewMode}
                        className="h-11 border-slate-200 bg-slate-50/50 transition-colors placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 focus:bg-white"
                        required
                      />
                    </div>
                    <div className="space-y-3">
                      <Label
                        htmlFor="sub_title"
                        className="text-sm font-semibold text-slate-700"
                      >
                        Sub Title
                      </Label>
                      <Input
                        id="sub_title"
                        name="sub_title"
                        value={formData.sub_title}
                        onChange={handleInputChange}
                        placeholder="e.g. High growth IT sector"
                        disabled={isViewMode}
                        className="h-11 border-slate-200 bg-slate-50/50 transition-colors placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 focus:bg-white"
                        required
                      />
                    </div>
                    <div className="space-y-3">
                      <Label
                        htmlFor="category"
                        className="block text-sm font-semibold text-slate-700"
                      >
                        Category
                      </Label>
                      <Select
                        value={formData.category}
                        onValueChange={val =>
                          handleSelectChange("category", val)
                        }
                        disabled={isViewMode}
                      >
                        <SelectTrigger
                          id="category"
                          className="h-11 w-full border-slate-200 bg-slate-50/50 text-left transition-colors focus:bg-white"
                        >
                          <SelectValue placeholder="Select Category" />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map(category => (
                            <SelectItem key={category.id} value={category.id}>
                              {category.title}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-3">
                      <Label className="block text-sm font-semibold text-slate-700">
                        Status
                      </Label>
                      <div className="flex items-center gap-3">
                        <Switch
                          checked={formData.enable}
                          onChange={checked =>
                            setFormData({ ...formData, enable: checked })
                          }
                          disabled={isViewMode}
                          style={{
                            backgroundColor: formData.enable
                              ? "#66A0CD"
                              : "#9ca3af",
                          }}
                        />
                        <span
                          className={`rounded-md px-2 py-1 text-xs font-semibold ${formData.enable ? "bg-[#66A0CD]/15 text-brandPrimary" : "bg-slate-200 text-slate-500"}`}
                        >
                          {formData.enable ? "Enabled" : "Disabled"}
                        </span>
                      </div>
                    </div>

                    <div className="col-span-1 space-y-3 md:col-span-2">
                      <div className="flex items-center justify-between">
                        <Label
                          htmlFor="query"
                          className="text-sm font-semibold text-slate-700"
                        >
                          Query
                        </Label>
                        {!isViewMode && (
                          <Button
                            type="button"
                            variant="default"
                            size="sm"
                            className={`h-8 gap-1.5 rounded-lg px-3 text-xs font-semibold shadow-sm ${isVerified ? "border border-green-200 bg-green-50 text-green-600 hover:bg-green-100 hover:text-green-700" : "bg-primary text-white hover:bg-primary/90"}`}
                            onClick={handleVerifyQuery}
                            disabled={
                              !formData.query || isVerifying || isVerified
                            }
                          >
                            {isVerifying ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : isVerified ? (
                              <CheckCircle className="h-3.5 w-3.5" />
                            ) : (
                              <PlayCircle className="h-3.5 w-3.5" />
                            )}
                            {isVerifying
                              ? "Verifying..."
                              : isVerified
                                ? "Verified"
                                : "Verify Query"}
                          </Button>
                        )}
                      </div>
                      <textarea
                        id="query"
                        name="query"
                        value={formData.query}
                        onChange={handleInputChange as any}
                        placeholder="Write query logic here..."
                        disabled={isViewMode}
                        rows={4}
                        className="min-h-[112px] w-full rounded-xl border border-slate-200 bg-slate-50/50 p-3 font-mono text-sm leading-relaxed transition-colors placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#66A0CD]/20"
                        required
                      />
                      <div className="mb-2 space-y-3 rounded-xl border border-slate-200/90 bg-white p-3 shadow-sm">
                        <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
                          {QUERY_OPERATOR_TOKENS.map(token => (
                            <Button
                              key={token}
                              type="button"
                              variant="outline"
                              size="sm"
                              disabled={isViewMode}
                              onClick={() => insertQueryToken(token)}
                              className="h-9 rounded-xl border-slate-200 bg-slate-50 px-0 text-[12px] font-semibold text-slate-600 transition-all hover:-translate-y-[1px] hover:border-[#66A0CD]/40 hover:bg-[#66A0CD]/5 hover:text-primary disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              {token}
                            </Button>
                          ))}
                        </div>

                        <div className="h-px w-full bg-slate-200" />

                        <div className="space-y-2">
                          <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-400">
                            Metrics
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {advancedQueryFields
                              .filter(field => field.is_enabled && field.title)
                              .map(field => (
                                <Button
                                  key={field.id}
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  disabled={isViewMode}
                                  onClick={() => insertQueryToken(field.title)}
                                  className="h-8 rounded-full border-slate-200 bg-slate-50 px-3 text-[12px] font-semibold text-slate-500 transition-all hover:-translate-y-[1px] hover:border-[#66A0CD]/40 hover:bg-[#66A0CD]/5 hover:text-primary disabled:cursor-not-allowed disabled:opacity-60"
                                >
                                  {field.title}
                                </Button>
                              ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </form>
      {(isVerifying || verifyPreviewRows.length > 0) && (
        <div className="m-6">
          <AntDTable
            showSettings
            data={verifyPreviewRows}
            columns={verifyColumns as any}
            rowKey="id"
            loading={isVerifying}
            pagination={{
              current: page,
              pageSize,
              total,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50", "100"],
              onChange: (nextPage, nextSize) => {
                setPage(nextPage);
                setPageSize(nextSize);
              },
            }}
            emptyText={
              isVerifying
                ? "Verifying query and loading data..."
                : "No data returned for this query"
            }
          />
        </div>
      )}
    </div>
  );
};

export default AddEditQueryMasterAdmin;
